import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { provideNativeDateAdapter } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withComponentInputBinding, withViewTransitions } from '@angular/router';

import { tenantHeaderInterceptor } from '@core/interceptors/tenant-header.interceptor';
import { ArcheryScoringAdapter } from '@core/sports/archery/archery-scoring.adapter';
import { createArcherySportDefinition } from '@core/sports/archery/archery.sport';
import { BaseballScoringAdapter } from '@core/sports/baseball/baseball-scoring.adapter';
import { createBaseballSportDefinition } from '@core/sports/baseball/baseball.sport';
import { BasketballScoringAdapter } from '@core/sports/basketball/basketball-scoring.adapter';
import { createBasketballSportDefinition } from '@core/sports/basketball/basketball.sport';
import { BasketballLiveScorerComponent } from '@core/sports/basketball/components/basketball-live-scorer.component';
import { BasketballScoringSetupComponent } from '@core/sports/basketball/components/basketball-scoring-setup.component';
import { BasketballSpectatorComponent } from '@core/sports/basketball/components/basketball-spectator.component';
import { SoftballScoringAdapter } from '@core/sports/softball/softball-scoring.adapter';
import { createSoftballSportDefinition } from '@core/sports/softball/softball.sport';
import { SPORT_REGISTRY } from '@core/sports/sport.registry';
import { TenantContextService } from '@core/tenant/tenant-context.service';

import { CoachScoringLivePageComponent } from '@features/coach/scoring-live/pages/coach-scoring-live-page.component';
import { CoachScoringSetupPageComponent } from '@features/coach/scoring-setup/pages/coach-scoring-setup-page.component';

import { routes } from './app.routes';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAppInitializer(() => inject(TenantContextService).init()),
    provideHttpClient(withInterceptors([tenantHeaderInterceptor])),
    provideRouter(routes, withComponentInputBinding(), withViewTransitions()),
    provideAnimations(),
    provideNativeDateAdapter(),
    {
      provide: SPORT_REGISTRY,
      multi: true,
      useFactory: (scoring: ArcheryScoringAdapter) =>
        createArcherySportDefinition({
          scoring,
          setupComponent: CoachScoringSetupPageComponent,
          liveComponent: CoachScoringLivePageComponent
        }),
      deps: [ArcheryScoringAdapter]
    },
    {
      provide: SPORT_REGISTRY,
      multi: true,
      useFactory: (scoring: BasketballScoringAdapter) =>
        createBasketballSportDefinition({
          scoring,
          setupComponent: BasketballScoringSetupComponent,
          liveComponent: BasketballLiveScorerComponent,
          spectatorComponent: BasketballSpectatorComponent
        }),
      deps: [BasketballScoringAdapter]
    },
    {
      provide: SPORT_REGISTRY,
      multi: true,
      useFactory: (scoring: BaseballScoringAdapter) => createBaseballSportDefinition({ scoring }),
      deps: [BaseballScoringAdapter]
    },
    {
      provide: SPORT_REGISTRY,
      multi: true,
      useFactory: (scoring: SoftballScoringAdapter) => createSoftballSportDefinition({ scoring }),
      deps: [SoftballScoringAdapter]
    }
  ]
};
